import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtErr, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDialog, ActivationModePicker, copyText } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Copy, ExternalLink, MoreHorizontal, Search, Trash2 } from "lucide-react";
import { motion } from "framer-motion";

const STATUS_LABELS = { active: "Active", suspended: "Suspendue", archived: "Archivée" };
const STATUS_STYLES = {
  active: "bg-green-100 text-green-900 dark:bg-green-950 dark:text-green-300",
  suspended: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-300",
  archived: "bg-neutral-200 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400",
};

export function CompanyStatusBadge({ status }) {
  return <span className={`rounded-sm px-2 py-0.5 text-xs font-semibold ${STATUS_STYLES[status] || STATUS_STYLES.active}`}>{STATUS_LABELS[status] || status}</span>;
}

export default function CompaniesPage() {
  const [companies, setCompanies] = useState(null);
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", plan: "decouverte",
    admin_first_name: "", admin_last_name: "", admin_email: "", template_sector: "",
    admin_activation_mode: "invite", admin_password: "", admin_password_confirm: "" });
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("active");
  const [fPlan, setFPlan] = useState("");
  const [fCity, setFCity] = useState("");
  const [sort, setSort] = useState("created_desc");
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [templates, setTemplates] = useState([]);
  const { user } = useAuth();
  const canDelete = user?.role === "super_admin" && user?.user_type === "staff_suivio";
  const [selected, setSelected] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const sectors = useMemo(() => [...new Set(templates.map((t) => t.sector))].sort(), [templates]);

  const load = () => api.get("/companies").then(({ data }) => setCompanies(data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => {
    load();
    api.get("/category-templates").then(({ data }) => setTemplates(data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const cities = useMemo(() => [...new Set((companies || []).map((c) => c.city).filter(Boolean))].sort(), [companies]);

  const visible = useMemo(() => {
    let list = companies || [];
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(s) || c.slug.includes(s));
    }
    if (fStatus) list = list.filter((c) => c.status === fStatus);
    if (fPlan) list = list.filter((c) => c.plan === fPlan);
    if (fCity) list = list.filter((c) => c.city === fCity);
    const sorted = [...list];
    if (sort === "created_asc") sorted.sort((a, b) => a.created_at.localeCompare(b.created_at));
    else if (sort === "users") sorted.sort((a, b) => b.users_count - a.users_count);
    else if (sort === "clients") sorted.sort((a, b) => b.clients_count - a.clients_count);
    else if (sort === "tickets") sorted.sort((a, b) => b.tickets_count - a.tickets_count);
    else sorted.sort((a, b) => b.created_at.localeCompare(a.created_at));
    return sorted;
  }, [companies, search, fStatus, fPlan, fCity, sort]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/companies", {
        name: form.name, email: form.email || null, phone: form.phone || null, city: form.city || null,
        plan: form.plan, template_sector: form.template_sector || null,
        admin: {
          first_name: form.admin_first_name, last_name: form.admin_last_name, email: form.admin_email,
          activation_mode: form.admin_activation_mode,
          password: form.admin_activation_mode === "manual" ? form.admin_password : null,
          password_confirm: form.admin_activation_mode === "manual" ? form.admin_password_confirm : null,
        },
      });
      setCreated(data);
      setOpen(false);
      load();
      toast.success(`Société ${data.name} créée`);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const canSubmitCompany = form.name && form.admin_first_name && form.admin_last_name && form.admin_email
    && (form.admin_activation_mode === "invite" || (form.admin_password.length >= 8 && form.admin_password === form.admin_password_confirm));

  const doSuspend = async () => {
    try {
      await api.patch(`/companies/${suspendTarget.id}`, { status: "suspended", suspend_reason: reason || null });
      toast.success("Société suspendue");
      setSuspendTarget(null); setReason("");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doReactivate = async (c) => {
    try {
      await api.patch(`/companies/${c.id}`, { status: "active" });
      toast.success("Société réactivée");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doArchive = async () => {
    try {
      await api.patch(`/companies/${archiveTarget.id}`, { status: "archived" });
      toast.success("Société archivée");
      setArchiveTarget(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const toggleOne = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const visibleIds = visible.map((c) => c.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
  const toggleAll = () => setSelected(allSelected ? [] : visibleIds);

  const doDelete = async () => {
    try {
      await api.delete(`/companies/${deleteTarget.id}`);
      toast.success(`Société ${deleteTarget.name} supprimée`);
      setSelected((s) => s.filter((x) => x !== deleteTarget.id));
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doBulkDelete = async () => {
    try {
      const { data } = await api.post("/companies/bulk-delete", { ids: selected });
      toast.success(`${data.deleted} société(s) supprimée(s)`);
      setSelected([]);
      setBulkOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="companies-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Entreprises</h1>
          <p className="mt-1 text-sm text-muted-foreground">Sociétés clientes du SaaS — isolation stricte par société.</p>
        </div>
        <Button data-testid="create-company-button" onClick={() => setOpen(true)} className="rounded-sm bg-primary text-white hover:bg-red-700">
          <Plus className="mr-2 h-4 w-4" /> Nouvelle société
        </Button>
      </div>

      <div className="mb-5 flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="search-companies" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une société…" className="w-64 rounded-sm pl-9" />
        </div>
        <Select value={fStatus} onValueChange={(v) => setFStatus(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-company-status" className="w-44 rounded-sm"><SelectValue placeholder="Statut : actives" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Statut : toutes</SelectItem>
            <SelectItem value="active">Actives</SelectItem>
            <SelectItem value="suspended">Suspendues</SelectItem>
            <SelectItem value="archived">Archivées</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fPlan} onValueChange={(v) => setFPlan(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-company-plan" className="w-44 rounded-sm"><SelectValue placeholder="Formule : toutes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Formule : toutes</SelectItem>
            <SelectItem value="decouverte">Découverte</SelectItem>
            <SelectItem value="croissance">Croissance</SelectItem>
            <SelectItem value="sur_mesure">Sur Mesure</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fCity} onValueChange={(v) => setFCity(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-company-city" className="w-44 rounded-sm"><SelectValue placeholder="Ville : toutes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Ville : toutes</SelectItem>
            {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="sort-companies" className="w-52 rounded-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="created_desc">Tri : création récente</SelectItem>
            <SelectItem value="created_asc">Tri : création ancienne</SelectItem>
            <SelectItem value="users">Tri : nb utilisateurs</SelectItem>
            <SelectItem value="clients">Tri : nb clients</SelectItem>
            <SelectItem value="tickets">Tri : nb tickets</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {canDelete && selected.length > 0 && (
        <div data-testid="companies-bulk-bar" className="mb-3 flex items-center justify-between rounded-sm border border-primary/40 bg-primary/5 px-4 py-2.5">
          <span className="text-sm font-medium" data-testid="companies-bulk-count">{selected.length} société(s) sélectionnée(s)</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setSelected([])} data-testid="companies-bulk-clear">Annuler</Button>
            <Button size="sm" className="rounded-sm bg-primary text-white hover:bg-red-700" onClick={() => setBulkOpen(true)} data-testid="companies-bulk-delete-button">
              <Trash2 className="mr-2 h-4 w-4" /> Supprimer la sélection
            </Button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-sm border border-border bg-card">
        <table className="w-full text-sm" data-testid="companies-table">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              {canDelete && (
                <th className="px-5 py-3 w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="companies-select-all" aria-label="Tout sélectionner" />
                </th>
              )}
              <th className="px-5 py-3 font-semibold">Société</th>
              <th className="px-5 py-3 font-semibold">Portail</th>
              <th className="px-5 py-3 font-semibold">Formule</th>
              <th className="px-5 py-3 font-semibold">Statut</th>
              <th className="px-5 py-3 font-semibold">Utilisateurs</th>
              <th className="px-5 py-3 font-semibold">Clients</th>
              <th className="px-5 py-3 font-semibold">Tickets</th>
              <th className="px-5 py-3 font-semibold">Créée le</th>
              <th className="px-5 py-3 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c, i) => (
              <motion.tr key={c.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                className="border-b border-border last:border-b-0 hover:bg-accent transition-colors duration-200" data-testid={`company-row-${c.slug}`}>
                {canDelete && (
                  <td className="px-5 py-3">
                    <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggleOne(c.id)}
                      data-testid={`company-select-${c.slug}`} aria-label={`Sélectionner ${c.name}`} />
                  </td>
                )}
                <td className="px-5 py-3">
                  <Link to={`/app/companies/${c.id}`} data-testid={`company-link-${c.slug}`} className="font-semibold hover:text-primary">{c.name}</Link>
                  <div className="text-xs text-muted-foreground">{c.city || "—"}</div>
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-muted-foreground">/{c.slug}</code>
                    <button data-testid={`copy-portal-${c.slug}`} title="Copier le lien portail" className="text-muted-foreground hover:text-primary transition-colors"
                      onClick={() => copyText(c.portal_url)}>
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                    <a data-testid={`open-portal-${c.slug}`} href={c.portal_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary transition-colors" title="Ouvrir le portail">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </td>
                <td className="px-5 py-3 capitalize">{c.plan}</td>
                <td className="px-5 py-3"><CompanyStatusBadge status={c.status} /></td>
                <td className="px-5 py-3">{c.users_count}</td>
                <td className="px-5 py-3">{c.clients_count}</td>
                <td className="px-5 py-3">{c.tickets_count}</td>
                <td className="px-5 py-3 text-muted-foreground">{fmtDate(c.created_at)}</td>
                <td className="px-5 py-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`company-actions-${c.slug}`} className="rounded-sm border border-border p-1.5 text-muted-foreground hover:text-foreground transition-colors">
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-sm">
                      <DropdownMenuItem asChild data-testid={`company-action-view-${c.slug}`}>
                        <Link to={`/app/companies/${c.id}`}>Voir le détail</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild data-testid={`company-action-edit-${c.slug}`}>
                        <Link to={`/app/companies/${c.id}`}>Modifier</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`company-action-copy-${c.slug}`}
                        onClick={() => copyText(c.portal_url)}>
                        Copier l'URL portail
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild data-testid={`company-action-open-${c.slug}`}>
                        <a href={c.portal_url} target="_blank" rel="noreferrer">Ouvrir le portail</a>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {c.status !== "active" && (
                        <DropdownMenuItem data-testid={`company-action-reactivate-${c.slug}`} onClick={() => doReactivate(c)}>
                          Réactiver
                        </DropdownMenuItem>
                      )}
                      {c.status === "active" && (
                        <DropdownMenuItem data-testid={`company-action-suspend-${c.slug}`} onClick={() => setSuspendTarget(c)} className="text-primary">
                          Suspendre
                        </DropdownMenuItem>
                      )}
                      {c.status !== "archived" && (
                        <DropdownMenuItem data-testid={`company-action-archive-${c.slug}`} onClick={() => setArchiveTarget(c)} className="text-primary">
                          Archiver
                        </DropdownMenuItem>
                      )}
                      {canDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem data-testid={`company-action-delete-${c.slug}`} onClick={() => setDeleteTarget(c)} className="text-primary font-medium">
                            <Trash2 className="mr-2 h-4 w-4" /> Supprimer définitivement
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
        {companies && visible.length === 0 && <div className="p-10 text-center text-sm text-muted-foreground">Aucune société avec ces filtres.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg" data-testid="create-company-dialog">
          <DialogHeader>
            <DialogTitle>Nouvelle société cliente</DialogTitle>
            <DialogDescription>Le slug du portail sera généré automatiquement depuis le nom.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom de la société *</Label>
              <Input data-testid="company-name-input" required value={form.name} onChange={set("name")} className="rounded-sm" placeholder="Ex : Agua Propreté" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email de contact</Label>
                <Input data-testid="company-email-input" type="email" value={form.email} onChange={set("email")} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input data-testid="company-phone-input" value={form.phone} onChange={set("phone")} className="rounded-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ville</Label>
                <Input data-testid="company-city-input" value={form.city} onChange={set("city")} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label>Formule</Label>
                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                  <SelectTrigger data-testid="company-plan-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="decouverte">Découverte</SelectItem>
                    <SelectItem value="croissance">Croissance</SelectItem>
                    <SelectItem value="sur_mesure">Sur Mesure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Modèle de catégories (secteur d'activité)</Label>
              <Select value={form.template_sector} onValueChange={(v) => setForm({ ...form, template_sector: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="company-sector-select" className="rounded-sm"><SelectValue placeholder="Aucun — démarrer sans catégories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun — démarrer sans catégories</SelectItem>
                  {sectors.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`sector-option-${s}`}>
                      {s} ({templates.filter((t) => t.sector === s).length} catégories)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.template_sector && (
                <p className="text-xs text-muted-foreground">
                  Seront créées : {templates.filter((t) => t.sector === form.template_sector).map((t) => t.name).join(" · ")}
                </p>
              )}
            </div>
            <div className="border-t border-border pt-4">
              <div className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Compte Admin Société</div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom *</Label>
                  <Input data-testid="admin-first-name-input" required value={form.admin_first_name} onChange={set("admin_first_name")} className="rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input data-testid="admin-last-name-input" required value={form.admin_last_name} onChange={set("admin_last_name")} className="rounded-sm" />
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <Label>Email admin *</Label>
                <Input data-testid="admin-email-input" type="email" required value={form.admin_email} onChange={set("admin_email")} className="rounded-sm" />
              </div>
              <div className="mt-4">
                <ActivationModePicker testid="admin-activation"
                  mode={form.admin_activation_mode} onModeChange={(v) => setForm({ ...form, admin_activation_mode: v })}
                  password={form.admin_password} onPasswordChange={(v) => setForm({ ...form, admin_password: v })}
                  confirm={form.admin_password_confirm} onConfirmChange={(v) => setForm({ ...form, admin_password_confirm: v })} />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="company-submit-button" type="submit" disabled={saving || !canSubmitCompany} className="rounded-sm bg-primary text-white hover:bg-red-700">
                {saving ? "Création…" : "Créer la société"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!created} onOpenChange={() => setCreated(null)}>
        <DialogContent className="rounded-sm" data-testid="company-created-dialog">
          <DialogHeader>
            <DialogTitle>Société créée</DialogTitle>
            <DialogDescription>Communiquez ces éléments à l'administrateur de la société.</DialogDescription>
          </DialogHeader>
          {created && (
            <div className="min-w-0 space-y-3 text-sm">
              <div className="overflow-hidden rounded-sm border border-border p-4">
                <div className="text-xs text-muted-foreground">URL portail client</div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <code data-testid="created-portal-url" className="min-w-0 flex-1 truncate font-semibold text-primary">{created.portal_url}</code>
                  <Button size="sm" variant="outline" data-testid="created-copy-url-button" className="shrink-0 rounded-sm"
                    onClick={() => copyText(created.portal_url)}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div className="overflow-hidden rounded-sm border border-border p-4">
                <div className="text-xs text-muted-foreground">Compte admin — {created.admin_status === "pending" ? "En attente d'activation" : "Actif"}</div>
                <div className="mt-1 font-medium">{created.contact?.email || ""}</div>
                {created.admin_activation_link ? (
                  <div className="mt-2">
                    <div className="text-xs text-muted-foreground">Lien d'activation (valide 72 h)</div>
                    <div className="mt-1 flex items-center gap-2">
                      <code data-testid="created-activation-link" className="min-w-0 flex-1 truncate rounded-sm bg-muted px-2 py-1 text-xs">{created.admin_activation_link}</code>
                      <Button size="sm" variant="outline" data-testid="created-copy-link-button" className="shrink-0 rounded-sm"
                        onClick={() => copyText(created.admin_activation_link)}>
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">Mot de passe défini manuellement — le compte peut se connecter immédiatement.</p>
                )}
                <p className="mt-2 text-xs text-muted-foreground" data-testid="created-email-note">
                  {created.email_status === "envoye" ? "Email d'invitation envoyé via Brevo — le lien ci-dessus n'est qu'un secours."
                    : created.email_status === "erreur" ? "Échec d'envoi de l'email d'invitation — transmettez le lien ci-dessus ou réessayez depuis les logs."
                    : "Email d'invitation simulé (aucun envoi réel) — transmettez le lien ci-dessus à l'administrateur."}
                </p>
                {created.categories_created > 0 && (
                  <p className="mt-2 text-xs font-medium text-green-700 dark:text-green-400" data-testid="created-categories-note">
                    {created.categories_created} catégories de tickets créées depuis le modèle sectoriel.
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!suspendTarget} onOpenChange={() => setSuspendTarget(null)}>
        <DialogContent className="rounded-sm" data-testid="suspend-dialog">
          <DialogHeader>
            <DialogTitle>Suspendre {suspendTarget?.name} ?</DialogTitle>
            <DialogDescription>
              Les admins, collaborateurs et clients entreprise perdront l'accès immédiatement. Les données restent conservées et consultables par le Super Admin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motif (optionnel)</Label>
            <Input data-testid="suspend-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-sm" placeholder="Ex : impayé, pause contrat…" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspendTarget(null)} className="rounded-sm">Annuler</Button>
            <Button data-testid="confirm-suspend-button" onClick={doSuspend} className="rounded-sm bg-primary text-white hover:bg-red-700">Suspendre</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!archiveTarget} onOpenChange={() => setArchiveTarget(null)}
        title={`Archiver ${archiveTarget?.name} ?`}
        description="La société et ses accès seront désactivés. Les données restent conservées et la société peut être réactivée. Action journalisée."
        confirmLabel="Archiver" onConfirm={doArchive} testid="archive-company-dialog" />

      <ConfirmDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}
        title={`Supprimer définitivement ${deleteTarget?.name} ?`}
        description="Cette action est IRRÉVERSIBLE. La société et TOUTES ses données liées (utilisateurs, clients, tickets, messages, catégories, fichiers, notifications) seront définitivement supprimées. Action journalisée."
        confirmLabel="Supprimer définitivement" onConfirm={doDelete} testid="delete-company-dialog" />

      <ConfirmDialog open={bulkOpen} onOpenChange={setBulkOpen}
        title={`Supprimer ${selected.length} société(s) ?`}
        description="Cette action est IRRÉVERSIBLE. Chaque société sélectionnée et TOUTES ses données liées (utilisateurs, clients, tickets, messages, catégories, fichiers) seront définitivement supprimées. Action journalisée."
        confirmLabel="Supprimer la sélection" onConfirm={doBulkDelete} testid="bulk-delete-companies-dialog" />
    </div>
  );
}
